import React from 'react';
import { motion } from 'framer-motion';
import { Check, Sparkles } from 'lucide-react';

/**
 * Top progress banner shown when the user opened the configurator via
 * /configurator?bundle=<id>. Renders three lines:
 *   1. Bundle name + overall position ("Familie-pakket — product 3 of 7")
 *   2. Per-type progress in orange ("Spannrahmen: 2 of 4 to enter")
 *   3. Overall remaining counter + double-tap hint
 *
 * When the bundle is fully captured (`remaining === 0`) it morphs into a
 * green completion celebration instead — kept in the same component to
 * avoid layout shift.
 */
export default function BundleBanner({ t, bundleInfo, cart, activeType, productName, typeProgress, remaining }) {
  if (!bundleInfo) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
      data-testid="cfg-bundle-banner"
      className={`mb-6 flex items-start gap-3 rounded-2xl px-4 py-3 ${
        remaining === 0
          ? 'bg-[#7FC242]/15 border border-[#7FC242]/40'
          : 'bg-[#F26B1A]/10 border border-[#F26B1A]/30'
      }`}
    >
      <span className={`inline-flex w-8 h-8 rounded-full text-white items-center justify-center shrink-0 ${
        remaining === 0 ? 'bg-[#7FC242]' : 'bg-[#F26B1A]'
      }`}>
        {remaining === 0 ? <Check className="w-4 h-4" strokeWidth={3} /> : <Sparkles className="w-4 h-4" />}
      </span>
      <div className="min-w-0 flex-1">
        {remaining === 0 ? (
          <>
            <p className="text-[13.5px] font-bold text-[#1F1D1A] leading-snug">{t.bundleCompleteTitle}</p>
            <p className="text-[12px] text-[#5C5852] mt-1">{t.bundleCompleteSub}</p>
          </>
        ) : (
          <>
            <p className="text-[13.5px] font-bold text-[#1F1D1A] leading-snug">
              {t.bundleBannerFmt
                .replace('{name}', bundleInfo.name)
                .replace('{current}', String(cart.length + 1))
                .replace('{total}', String(bundleInfo.total))}
            </p>
            {typeProgress && (
              <p className="text-[12.5px] font-semibold text-[#F26B1A] mt-1" data-testid="cfg-bundle-type-progress">
                {t.bundleTypeProgressFmt
                  .replace('{type}', productName || activeType?.slug || '')
                  .replace('{n}', String(typeProgress.current))
                  .replace('{qty}', String(typeProgress.total))}
              </p>
            )}
            <p className="text-[12px] text-[#5C5852] mt-1" data-testid="cfg-bundle-remaining">
              {t.bundleRemainingFmt
                .replace('{remaining}', String(remaining))
                .replace('{total}', String(bundleInfo.total))}
            </p>
            <p className="text-[11.5px] text-[#5C5852]/80 italic mt-1">{t.bundleHintMore}</p>
          </>
        )}
      </div>
    </motion.div>
  );
}
